import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { FileWarning, Download, FileText, Loader2, Search } from 'lucide-react';
import { accountingAPI, carrierAPI } from '@/lib/api';
import { toast } from 'sonner';

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const MONTHS = ['01','02','03','04','05','06','07','08','09','10','11','12'];

export default function UnreceivedCommissionPage() {
  const [carriers, setCarriers] = useState([]);
  const [filters, setFilters] = useState({ carrier_id: '', period_year: '', period_month: '', agent: '' });
  const [data, setData] = useState({ rows: [], total_amount: 0, count: 0, generated_at: '' });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState('');

  useEffect(() => {
    (async () => {
      try { const { data } = await carrierAPI.list(); setCarriers(data || []); } catch { /* noop */ }
    })();
  }, []);

  const queryParams = useMemo(() => {
    const p = {};
    if (filters.carrier_id) p.carrier_id = filters.carrier_id;
    if (filters.period_year) p.period_year = filters.period_year;
    if (filters.period_month) p.period_month = filters.period_month;
    if (filters.agent.trim()) p.agent = filters.agent.trim();
    return p;
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await accountingAPI.unreceivedReport(queryParams);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load report');
    } finally { setLoading(false); }
  }, [queryParams]);

  useEffect(() => { load(); }, [load]);

  const exportFile = async (format) => {
    setExporting(format);
    try {
      const { data: blob } = await accountingAPI.unreceivedReportFile({ ...queryParams, format });
      const url = window.URL.createObjectURL(new Blob([blob]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `unreceived-commission.${format === 'csv' ? 'csv' : 'pdf'}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(`Failed to export ${format.toUpperCase()}`);
    } finally { setExporting(''); }
  };

  const years = [2024, 2025, 2026, 2027];

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-5" data-testid="unreceived-commission-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-semibold flex items-center gap-2">
            <FileWarning size={22} className="text-amber-600" /> Unreceived Commission
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Policies on statements whose carrier deposit is <b>not confirmed</b> in the bank
            (KR has not received the money). For audit with upline accounting.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => exportFile('csv')} disabled={exporting || !data.count} data-testid="export-csv-btn">
            {exporting === 'csv' ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Download size={14} className="mr-1" />} Excel / CSV
          </Button>
          <Button variant="outline" onClick={() => exportFile('pdf')} disabled={exporting || !data.count} data-testid="export-pdf-btn">
            {exporting === 'pdf' ? <Loader2 size={14} className="mr-1 animate-spin" /> : <FileText size={14} className="mr-1" />} PDF
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="py-4 grid gap-3 md:grid-cols-4">
          <div>
            <Label className="text-xs">Carrier</Label>
            <Select value={filters.carrier_id || 'all'} onValueChange={(v) => setFilters((f) => ({ ...f, carrier_id: v === 'all' ? '' : v }))}>
              <SelectTrigger data-testid="filter-carrier"><SelectValue placeholder="All carriers" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All carriers</SelectItem>
                {carriers.map((c) => <SelectItem key={c.carrier_id} value={c.carrier_id}>{c.carrier_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Year</Label>
            <Select value={filters.period_year || 'all'} onValueChange={(v) => setFilters((f) => ({ ...f, period_year: v === 'all' ? '' : v }))}>
              <SelectTrigger data-testid="filter-year"><SelectValue placeholder="All years" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All years</SelectItem>
                {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Month</Label>
            <Select value={filters.period_month || 'all'} onValueChange={(v) => setFilters((f) => ({ ...f, period_month: v === 'all' ? '' : v }))}>
              <SelectTrigger data-testid="filter-month"><SelectValue placeholder="All months" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All months</SelectItem>
                {MONTHS.map((m) => <SelectItem key={m} value={String(Number(m))}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Agent (name or NPN)</Label>
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
              <Input value={filters.agent} onChange={(e) => setFilters((f) => ({ ...f, agent: e.target.value }))}
                placeholder="Search agent…" className="pl-8" data-testid="filter-agent" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between">
          <CardTitle className="text-base">
            {loading ? 'Loading…' : `${data.count} policies`}
          </CardTitle>
          <div className="text-sm text-slate-600">Total not received: <b className="text-amber-700" data-testid="report-total">{money(data.total_amount)}</b></div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" /></div>
          ) : data.count === 0 ? (
            <div className="py-10 text-center text-slate-400" data-testid="report-empty">
              No unreceived commissions — every matching statement's deposit is confirmed. 🎉
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-slate-500 border-b">
                    <th className="py-2 pr-2">Carrier</th>
                    <th className="py-2 pr-2">Period</th>
                    <th className="py-2 pr-2">Agent</th>
                    <th className="py-2 pr-2">Agency</th>
                    <th className="py-2 pr-2">Policy #</th>
                    <th className="py-2 pr-2">Member</th>
                    <th className="py-2 pr-2">Product</th>
                    <th className="py-2 pr-2">Effective</th>
                    <th className="py-2 pr-2 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={i} className="border-b border-slate-100" data-testid={`report-row-${i}`}>
                      <td className="py-1.5 pr-2">{r.carrier}</td>
                      <td className="py-1.5 pr-2">{r.period}</td>
                      <td className="py-1.5 pr-2">{r.agent_name}{r.agent_npn ? <span className="text-slate-400"> · {r.agent_npn}</span> : ''}</td>
                      <td className="py-1.5 pr-2">{r.agency}</td>
                      <td className="py-1.5 pr-2 font-mono">{r.policy_number}</td>
                      <td className="py-1.5 pr-2">{r.member}</td>
                      <td className="py-1.5 pr-2">{r.product}</td>
                      <td className="py-1.5 pr-2">{(r.effective_date || '').slice(0, 10)}</td>
                      <td className="py-1.5 pr-2 text-right font-medium">{money(r.amount_received_by_kr)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
